/**
 * Slot maps — which sections exist on a contended surface and what order
 * band each one occupies (architecture §5.4/§6.1).
 *
 * Composition owns the **map**; it never owns the **membership**. A domain
 * places itself with `section`/`order` metadata on its own contribution, and
 * `buildAppRegistry` validates that placement against the map here. So this
 * file changes when a *surface* gains a section — domain-creation frequency —
 * not when a team ships a screen.
 *
 * **Nav stays empty on purpose** and is explicitly design-gated by the repo's
 * sidebar-IA rule: a new top-level IA entry needs designer confirmation. This
 * file types the declaration; it does not grant the slot. Until an owner
 * declares sections, a nav contribution is *rejected* by the registry with a
 * named problem rather than silently placed — the correct failure for a surface
 * whose slot map does not exist yet. Queued modals are unaffected: they are
 * ordered by frozen numeric priority, not by a slot map.
 *
 * **Settings sections are declared** — they are the sidebar groups that exist
 * today, not new product arbitration. The settings sidebar
 * (`src/components/layout/Navigation/settings/hooks/useSettingsList.tsx`)
 * stays hand-written and design-owned: it has no ids and no ordering metadata,
 * and this map does not give it any. What the section ids buy is a *placement
 * vocabulary* for a contributed screen body, so a package can serve an existing
 * settings screen without touching the sidebar or the IA.
 */

export interface SlotMap {
  /** Declared sections, in display order. */
  readonly sections: readonly string[];
  /** Inclusive `[min, max]` order band per section. */
  readonly orderBands: Readonly<Record<string, readonly [number, number]>>;
}

/**
 * How a settings section id is recognised in the hand-written sidebar: the
 * i18n *source* of the group's label, never the rendered string (which is
 * localized). i18next groups carry a key; react-intl groups carry the message
 * id plus its `defaultMessage`.
 *
 * This is a constant rather than a comment so it can be asserted: the sidebar's
 * own suite renders `useSettingsList()` across the flag matrix and fails when a
 * group label it produces is not one of these.
 */
export type SettingsSectionLabelSource =
  | { readonly i18nextKey: string }
  | { readonly messageId: string; readonly defaultMessage: string };

/**
 * Section id → the sidebar group(s) it names, measured against
 * `useSettingsList.tsx`. Two sources for one section means the group's label
 * switches at runtime: `admin` reads "Team" or "Admin" depending on the
 * workspace-settings IA flag, which is a copy change, not two groups.
 */
export const SETTINGS_SECTION_SIDEBAR_LABELS: Readonly<
  Record<string, readonly SettingsSectionLabelSource[]>
> = {
  personal: [{ i18nextKey: 'sidenav.settings.personal' }],
  integrations: [
    { i18nextKey: 'sidenav.settings.integrations' },
    { messageId: '6cBDheQqr8', defaultMessage: 'Integrations' },
    { messageId: 'l9CREU56b3', defaultMessage: 'Connections' },
  ],
  workspace: [{ messageId: 'JAofjLlpYb', defaultMessage: 'Workspace' }],
  admin: [
    { i18nextKey: 'sidenav.settings.team' },
    { messageId: 'iHN12uxiqf', defaultMessage: 'Admin' },
  ],
  comms: [{ messageId: 'YpVoqnKc3L', defaultMessage: 'Comms' }],
};

/**
 * Display order for the declared sections. Only the registry's own sort reads
 * it — screens are served by id, so this never moves a sidebar group.
 *
 * It cannot match the sidebar in both flag states and does not try to:
 * `integrations` renders directly below `personal` under the workspace-settings
 * IA and last without it, and the `workspace` group only exists under the IA.
 * The order below is the IA-on one, the direction the product is moving.
 */
const SETTINGS_SECTIONS = [
  'personal',
  'integrations',
  'workspace',
  'admin',
  'comms',
] as const;

/**
 * One wide band per section to start. A band exists so a section *can* be
 * ordered later without every contributor renumbering; splitting `[0, 999]`
 * into meaningful ranges is a product decision for whoever owns the section,
 * and an unbanded `order` is rejected by the registry either way.
 */
const SETTINGS_ORDER_BAND: readonly [number, number] = [0, 999];

export const settingsSlotMap: SlotMap = {
  sections: SETTINGS_SECTIONS,
  orderBands: Object.fromEntries(
    SETTINGS_SECTIONS.map((section) => [section, SETTINGS_ORDER_BAND])
  ),
};

export const navSlotMap: SlotMap = {
  sections: [],
  orderBands: {},
};
